import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { MapPin, Mail, Phone, CheckCircle2, Building2, Shield, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Consultation type configurations
const CONSULTATION_TYPES = {
    general: {
        title: "Talk to Our Experts",
        subtitle: "Enterprise Engagement",
        description: "Whether you're planning a cloud migration, private cloud deployment, disaster-recovery strategy, or compliance-driven architecture, our solution architects and engineers are here to help.",
        formTitle: "Secure Inquiry Form",
        buttonText: "Submit Inquiry",
        successMessage: "Inquiry received. Our team will respond promptly with the appropriate technical or commercial contact."
    },
    demo: {
        title: "Request a Demo",
        subtitle: "See EverestCloud in Action",
        description: "Schedule a personalized demo with our solutions team to see how EverestCloud can transform your infrastructure.",
        formTitle: "Demo Request Form",
        buttonText: "Request Demo",
        successMessage: "Demo request received! Our team will contact you within 24 hours."
    },
    security_assessment: {
        title: "Security Assessment",
        subtitle: "Enterprise Security Review",
        description: "Request a comprehensive security assessment from our certified security architects to evaluate your infrastructure needs.",
        formTitle: "Security Assessment Request",
        buttonText: "Request Assessment",
        successMessage: "Security assessment request submitted. A security architect will reach out shortly."
    },
    compliance_review: {
        title: "Compliance Review",
        subtitle: "Regulatory Alignment Check",
        description: "Our compliance experts will review your requirements and demonstrate how EverestCloud meets regulatory standards.",
        formTitle: "Compliance Review Request",
        buttonText: "Request Review",
        successMessage: "Compliance review request received. Our compliance team will contact you."
    },
    architecture_walkthrough: {
        title: "Architecture Walkthrough",
        subtitle: "Deep Dive into EverestCloud",
        description: "Request a confidential architecture walkthrough to see how EverestCloud protects your most critical workloads.",
        formTitle: "Walkthrough Request",
        buttonText: "Request Walkthrough",
        successMessage: "Architecture walkthrough requested. We'll schedule a session with our solutions team."
    },
    migration_consultation: {
        title: "Migration Consultation",
        subtitle: "Seamless Cloud Migration",
        description: "Talk to our migration experts about moving your workloads to EverestCloud with minimal disruption.",
        formTitle: "Migration Consultation Request",
        buttonText: "Request Consultation",
        successMessage: "Migration consultation request received. Our migration experts will contact you."
    }
};

const Contact = () => {
    const [searchParams] = useSearchParams();
    const typeParam = searchParams.get('type') || 'general';
    const consultationType = CONSULTATION_TYPES[typeParam] ? typeParam : 'general';
    const config = CONSULTATION_TYPES[consultationType];

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    // Validation helpers
    const validateName = (name) => {
        const nameRegex = /^[a-zA-Z\s\-'\.]+$/;
        return name.trim().length >= 2 && name.trim().length <= 50 && nameRegex.test(name.trim());
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return email.trim().length <= 100 && emailRegex.test(email.trim());
    };

    const validatePhone = (phone) => {
        if (!phone.trim()) return true;
        const phoneRegex = /^[\d\s\+\-\(\)]+$/;
        return phone.trim().length >= 7 && phone.trim().length <= 20 && phoneRegex.test(phone.trim());
    };

    const validateCompany = (company) => {
        const companyRegex = /^[a-zA-Z0-9\s\-\.\,\&\(\)\']+$/;
        return company.trim().length >= 2 && company.trim().length <= 100 && companyRegex.test(company.trim());
    };

    const validateMessage = (message) => {
        if (!message.trim()) return false;
        return message.trim().length >= 10 && message.trim().length <= 2000;
    };

    // Sanitize input to prevent XSS
    const sanitizeInput = (input) => {
        if (!input) return '';
        return input
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .trim();
    };

    // Industry options
    const industryOptions = [
        { value: '', label: 'Select Industry' },
        { value: 'banking', label: 'Banking & Financial Services' },
        { value: 'government', label: 'Government & Public Sector' },
        { value: 'telecom', label: 'Telecom & ISP' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'retail', label: 'Retail & E-commerce' },
        { value: 'education', label: 'Education' },
        { value: 'other', label: 'Other' }
    ];

    // Reset form when consultation type changes
    useEffect(() => {
        setIsSubmitted(false);
    }, [consultationType]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
        if (errors[id]) {
            setErrors({ ...errors, [id]: '' });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!validateName(formData.firstName)) {
            newErrors.firstName = 'Please enter a valid first name (2-50 letters only)';
        }
        if (!validateName(formData.lastName)) {
            newErrors.lastName = 'Please enter a valid last name (2-50 letters only)';
        }
        if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number (7-20 digits)';
        }
        if (!validateCompany(formData.company)) {
            newErrors.company = 'Please enter a valid company name (2-100 characters)';
        }
        if (!validateMessage(formData.message)) {
            newErrors.message = 'Please enter a message (10-2000 characters)';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error("Please fix the errors in the form");
            return;
        }
        
        setIsSubmitting(true);
        try {
            const payload = {
                consultation_type: consultationType,
                ...formData
            };
            await axios.post(`${API}/consultation`, payload);
            toast.success(config.successMessage);
            setIsSubmitted(true);
            setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', industry: '', message: '' });
            setErrors({});
        } catch (error) {
            console.error(error);
            toast.error("Failed to send request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <SEO page="contact" />
            <PageHeader 
                title="About" 
                highlight="EverestCloud"
                description="Nepal's sovereign enterprise cloud platform—engineered for organizations that demand performance, compliance, and data residency by design."
            />

            <section className="py-20 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4">

                    {/* About Section */}
                    <div className="max-w-6xl mx-auto mb-24">
                        <div className="grid md:grid-cols-3 gap-8">
                            <AboutCard 
                                Icon={Building2}
                                title="Enterprise-Focused by Default"
                                description="We are not a low-cost, shared hosting provider. EverestCloud delivers dedicated, guaranteed performance environments built for enterprise and regulated workloads."
                            />
                            <AboutCard 
                                Icon={Shield}
                                title="Nepal-Hosted & Sovereign"
                                description="All infrastructure is hosted within Nepal. Your data remains legally and physically within national borders, supporting strict regulatory and jurisdictional requirements."
                            />
                            <AboutCard 
                                Icon={Users}
                                title="Built by Integrators"
                                description="Designed and operated by Digital Network Solution (DNS)—experienced system integrators who understand legacy modernization, hybrid architectures, and complex enterprise migrations."
                            />
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="grid lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
                        
                        {/* Left Column - Contact Info */}
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
                                {config.title}
                            </h2>
                            {consultationType !== 'general' && (
                                <p className="text-accent font-medium uppercase tracking-wider text-sm mb-6">{config.subtitle}</p>
                            )}
                            <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed mb-4">
                                {config.description}
                            </p>
                            {consultationType === 'general' && (
                                <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed mb-8">
                                    We engage at both technical and executive levels to ensure alignment with your organization&apos;s goals.
                                </p>
                            )}
                            
                            {/* Contact Details */}
                            <div className="space-y-8 mt-10">
                                <div className="flex items-start gap-5">
                                    <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                                        <MapPin className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-900 dark:text-white text-lg">Corporate Headquarters</h4>
                                        <p className="text-slate-600 dark:text-slate-400 mt-1">
                                            EverestCloud<br />
                                            Kathmandu, Nepal<br />
                                            Bagmati Province
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-5">
                                    <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                                        <Mail className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-900 dark:text-white text-lg">Sales & Enterprise Inquiries</h4>
                                        <div className="text-slate-600 dark:text-slate-400 mt-1 space-y-1">
                                            <p>mailus@digitalnetwork.com.np</p>
                                            <p>info@everestcloud.com.np</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-5">
                                    <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                                        <Phone className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-900 dark:text-white text-lg">Phone</h4>
                                        <p className="text-slate-600 dark:text-slate-400 mt-1">+977-14537311</p>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Note */}
                            <div className="mt-10 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-white/5">
                                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                    For RFPs, compliance discussions, architecture reviews, or proof-of-concept requests, please reach out using the contact details above or the secure form.
                                </p>
                            </div>
                        </div>

                        {/* Right Column - Form */}
                        <div>
                            <Card className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/5 shadow-xl">
                                <CardContent className="p-8">
                                    {isSubmitted ? (
                                        <div className="text-center py-12">
                                            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <CheckCircle2 className="w-10 h-10 text-green-500" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Request Submitted!</h3>
                                            <p className="text-slate-600 dark:text-slate-400 mb-8">{config.successMessage}</p>
                                            <Button 
                                                onClick={() => setIsSubmitted(false)} 
                                                variant="outline"
                                                className="border-accent text-accent hover:bg-accent hover:text-black"
                                            >
                                                Submit Another Request
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">{config.formTitle}</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
                                                All inquiries submitted through this form are treated as confidential.
                                            </p>
                                            <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultation-form">
                                                <div className="grid md:grid-cols-2 gap-5">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="firstName" className="text-slate-600 dark:text-slate-400">First Name</Label>
                                                        <Input 
                                                            id="firstName" 
                                                            placeholder="Rajesh" 
                                                            value={formData.firstName}
                                                            onChange={handleChange}
                                                            maxLength={50}
                                                            className={`bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 h-11 ${errors.firstName ? 'border-red-400' : ''}`}
                                                            required
                                                            data-testid="input-firstName"
                                                        />
                                                        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="lastName" className="text-slate-600 dark:text-slate-400">Last Name</Label>
                                                        <Input 
                                                            id="lastName" 
                                                            placeholder="Sharma" 
                                                            value={formData.lastName}
                                                            onChange={handleChange}
                                                            maxLength={50}
                                                            className={`bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 h-11 ${errors.lastName ? 'border-red-400' : ''}`}
                                                            required
                                                            data-testid="input-lastName"
                                                        />
                                                        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="email" className="text-slate-600 dark:text-slate-400">Work Email</Label>
                                                    <Input 
                                                        id="email" 
                                                        type="email" 
                                                        placeholder="rajesh@company.com.np" 
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        maxLength={100}
                                                        className={`bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 h-11 ${errors.email ? 'border-red-400' : ''}`}
                                                        required
                                                        data-testid="input-email"
                                                    />
                                                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-5">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="phone" className="text-slate-600 dark:text-slate-400">Phone Number</Label>
                                                        <Input 
                                                            id="phone" 
                                                            type="tel"
                                                            placeholder="+977-98XXXXXXXX" 
                                                            value={formData.phone}
                                                            onChange={handleChange}
                                                            maxLength={20}
                                                            className={`bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 h-11 ${errors.phone ? 'border-red-400' : ''}`}
                                                            data-testid="input-phone"
                                                        />
                                                        {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="industry" className="text-slate-600 dark:text-slate-400">Industry</Label>
                                                        <select
                                                            id="industry"
                                                            value={formData.industry}
                                                            onChange={handleChange}
                                                            className="w-full h-11 px-3 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white rounded-md focus:border-accent/50 focus:outline-none"
                                                            data-testid="select-industry"
                                                        >
                                                            {industryOptions.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="company" className="text-slate-600 dark:text-slate-400">Company / Organization</Label>
                                                    <Input 
                                                        id="company" 
                                                        placeholder="Nepal Commercial Bank Limited" 
                                                        value={formData.company}
                                                        onChange={handleChange}
                                                        maxLength={100}
                                                        className={`bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 h-11 ${errors.company ? 'border-red-400' : ''}`}
                                                        required
                                                        data-testid="input-company"
                                                    />
                                                    {errors.company && <p className="text-xs text-red-500">{errors.company}</p>}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="message" className="text-slate-600 dark:text-slate-400">
                                                        {consultationType === 'general' ? 'How can we help?' : 'Tell us about your requirements'}
                                                    </Label>
                                                    <Textarea 
                                                        id="message" 
                                                        placeholder="Tell us about your cloud infrastructure requirements..."
                                                        className={`min-h-[130px] bg-white dark:bg-slate-900/50 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-accent/50 ${errors.message ? 'border-red-400' : ''}`}
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        maxLength={2000}
                                                        required
                                                        data-testid="textarea-message"
                                                    />
                                                    {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                                                </div>
                                                <Button 
                                                    type="submit" 
                                                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-lg font-bold rounded-full mt-2" 
                                                    disabled={isSubmitting}
                                                    data-testid="submit-button"
                                                >
                                                    {isSubmitting ? 'Submitting...' : config.buttonText}
                                                </Button>
                                                <p className="text-xs text-slate-500 dark:text-slate-500 text-center mt-4">
                                                    Our team will respond promptly with the appropriate technical or commercial contact.
                                                </p>
                                            </form>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Bottom Brand Section */}
                    <div className="mt-24 max-w-4xl mx-auto text-center">
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-10">
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png"
                                    alt="EverestCloud"
                                    className="h-10 w-10 object-contain dark:brightness-0 dark:invert brightness-0"
                                />
                                <span className="font-bold text-2xl tracking-tight text-slate-900 dark:text-white">
                                    Everest<span className="text-accent">Cloud</span>
                                </span>
                            </div>
                            <p className="text-accent font-medium text-sm uppercase tracking-widest mb-4">
                                Scalable. Reliable. Nepali.
                            </p>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto">
                                Nepal&apos;s Sovereign Enterprise Cloud Platform—engineered for the strict data-residency and compliance needs of banks, government, and regulated industries.
                            </p>
                        </div>
                    </div>

                </div>
            </section>
        </Layout>
    );
};

// About Card Component
const AboutCard = ({ Icon, title, description }) => (
    <div className="p-8 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-white/5 rounded-2xl hover:border-accent/30 transition-all duration-300 group">
        <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mb-5 group-hover:bg-accent group-hover:text-black transition-all duration-300">
            <Icon className="w-7 h-7" />
        </div>
        <h3 className="font-bold text-xl text-slate-900 dark:text-white mb-3">{title}</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
    </div>
);

export default Contact;
